import { action, computed, makeObservable, observable } from 'mobx';
import { Service } from 'typedi';

import { DEFAULT_PAGE_SIZE } from '../constants/announcement';
import type { IApiError } from '../services/apiError';
import type { IAnnouncementEntity, ITableSettings, TSort } from '../types/announcement';

export type TListState =
  | { status: 'loading' }
  | { status: 'loaded'; items: IAnnouncementEntity[] }
  | { status: 'failed'; error: IApiError };

@Service()
export class AnnouncementListStore {
  private _state: TListState = { status: 'loading' };
  private _sort: TSort = undefined;
  private _page = 1;
  private _pageSize = DEFAULT_PAGE_SIZE;
  private _withdrawing: IAnnouncementEntity | null = null;
  private _actionError: IApiError | null = null;
  /** Ids whose pin request runs: a second click is ignored and the controls are disabled. */
  private _pinning: string[] = [];

  constructor() {
    makeObservable<
      AnnouncementListStore,
      '_state' | '_sort' | '_page' | '_pageSize' | '_withdrawing' | '_actionError' | '_pinning'
    >(this, {
      _state: observable.ref,
      _sort: observable.ref,
      _page: observable,
      _pageSize: observable,
      _withdrawing: observable.ref,
      _actionError: observable.ref,
      _pinning: observable.ref,
      state: computed,
      sort: computed,
      page: computed,
      pageSize: computed,
      withdrawing: computed,
      actionError: computed,
      pinning: computed,
      reset: action,
      setLoading: action,
      setLoaded: action,
      setFailed: action,
      setSort: action,
      setPagination: action,
      setWithdrawing: action,
      setActionError: action,
      startPinning: action,
      finishPinning: action,
    });
  }

  get state(): TListState {
    return this._state;
  }

  get sort(): TSort {
    return this._sort;
  }

  get page(): number {
    return this._page;
  }

  get pageSize(): number {
    return this._pageSize;
  }

  /** The item whose withdraw confirmation is open. */
  get withdrawing(): IAnnouncementEntity | null {
    return this._withdrawing;
  }

  /** A failed pin or withdraw from the list. */
  get actionError(): IApiError | null {
    return this._actionError;
  }

  /** Ids whose pin request runs; table cells render outside the observer, so the screen reads this once per render. */
  get pinning(): string[] {
    return this._pinning;
  }

  isPinning = (id: string): boolean => this._pinning.includes(id);

  /** A new visit: loading, page 1, the table settings kept from earlier visits. */
  reset = (settings: ITableSettings): void => {
    this._state = { status: 'loading' };
    this._sort = settings.sort;
    this._page = 1;
    this._pageSize = settings.pageSize;
    this._withdrawing = null;
    this._actionError = null;
    this._pinning = [];
  };

  setLoading = (): void => {
    this._state = { status: 'loading' };
  };

  setLoaded = (items: IAnnouncementEntity[]): void => {
    this._state = { status: 'loaded', items };
  };

  setFailed = (error: IApiError): void => {
    this._state = { status: 'failed', error };
  };

  /** A new column sort starts from page 1. */
  setSort = (sort: TSort): void => {
    this._sort = sort;
    this._page = 1;
  };

  /** Pager change; a new page size starts from page 1. */
  setPagination = (page: number, pageSize: number): void => {
    this._page = pageSize === this._pageSize ? page : 1;
    this._pageSize = pageSize;
  };

  setWithdrawing = (item: IAnnouncementEntity | null): void => {
    this._withdrawing = item;
  };

  setActionError = (error: IApiError | null): void => {
    this._actionError = error;
  };

  startPinning = (id: string): void => {
    this._pinning = [...this._pinning, id];
  };

  finishPinning = (id: string): void => {
    this._pinning = this._pinning.filter(x => x !== id);
  };
}
